'use es6';

import { SLICED } from 'reporting-visualizations/constants/visualizationTypes';
import { SlicedVisualization } from 'reporting-visualizations/visualizations/sliced/slicedRecords';
import { getTotalMeasureAlias } from 'reporting-visualizations/visualizations/crossTab/dataHelpers/totals';
import { getReportColorEncoding, getReportValuesEncodings, getDateResolution } from '../../utils/report-utils';
import { getEncodingColumnAlias } from '../../utils/visual-utils';
import { computeAssignments, computePropertyAssignments } from '../color/assign';
import { collectDimensionBuckets, collectMeasureBuckets } from '../color/bucket';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { getColorPalette } from '../color/utils';
import { getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import I18n from 'I18n';

/**
 * @param {Column?} colorColumn
 * @param {List<Column>} measureColumns
 * @param {Dataset} dataset
 * @param {ColorOption} colorOption
 * @param {BrandKitSnowflakePalettes} brandKitPalettes
 * @returns high charts color encoding
 */
export const toColor = (colorColumn, measureColumns, dataset, colorOption, brandKitPalettes) => {
  const order = colorColumn && toVisualizationOrder(colorColumn, dataset);
  const buckets = colorColumn === undefined ? collectMeasureBuckets(measureColumns, dataset) : collectDimensionBuckets(colorColumn, dataset);
  const {
    scheme
  } = getColorPalette(colorOption, brandKitPalettes);
  const assignments = computeAssignments(buckets, scheme, colorOption.assignments);
  if (assignments.isEmpty()) {
    return undefined;
  }
  const patternAssignments = computePropertyAssignments(buckets, scheme.patterns);
  if (colorColumn === undefined) {
    return {
      default: assignments.first(),
      defined: assignments.toJS(),
      definedPatterns: patternAssignments && patternAssignments.toJS()
    };
  }
  const colorId = colorColumn.alias;
  return {
    field: colorId,
    defined: assignments.toJS(),
    definedPatterns: patternAssignments && patternAssignments.toJS(),
    order
  };
};
export const getGrandTotalColumn = report => {
  const values = getReportValuesEncodings(report);
  const valueColumns = values.map(v => v.column);
  const firstValueColumn = valueColumns.first();
  const totalAlias = firstValueColumn && getTotalMeasureAlias(firstValueColumn.alias, []);
  return totalAlias;
};
export const mapPieToVisual = (report, datasets, reportOptions, {
  brandKitPalettes
}) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const {
    showDataLabels,
    color: colorOption,
    showTotals,
    legendPosition,
    legendWidth
  } = report.visual.options;
  const color = getReportColorEncoding(report);
  const values = getReportValuesEncodings(report);
  const isGrouped = !!color;
  const value = isGrouped ? values.first() : undefined;
  const colorColumn = isGrouped && color.column;
  const valueColumns = values.map(v => v.column);
  const colorEncodingOptions = isGrouped ? color.options : undefined;
  const valueOptionsByColumn = values.map(v => {
    return {
      column: v.column.get('alias'),
      options: v.options
    };
  });
  const maybeDateResolution = colorColumn ? getDateResolution(colorColumn.get('alias'), report) : undefined;
  return SlicedVisualization({
    type: SLICED,
    data: {
      primary: primaryDataset
    },
    style: {
      dataLabels: showDataLabels,
      showTotals,
      legendPosition,
      legendWidth,
      dateResolution: maybeDateResolution
    },
    isGrouped,
    layer: {
      dataset: 'primary',
      color: toColor(colorColumn || undefined, valueColumns, primaryDataset, colorOption, brandKitPalettes),
      angle: isGrouped ? {
        field: getEncodingColumnAlias(value)
      } : {
        field: I18n.text('property.count.label')
      },
      colorFormattingOptions: colorEncodingOptions,
      valueFormattingOptions: valueOptionsByColumn,
      style: {
        innerSize: '0%'
      },
      grandTotalColumn: getGrandTotalColumn(report)
    }
  });
};
export const __TESTABLE__ = {
  toColor
};